import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Select — Radix's listbox, wrapped.
 *
 * We own the wrapper, not the primitive: Radix handles the keyboard, the typeahead, the focus
 * return and the `aria-activedescendant` bookkeeping, and this file decides what a Clavian select
 * looks like, what its placeholder says and where its strings come from.
 *
 * The options are a prop rather than children on purpose — it keeps a caller from building a
 * listbox out of divs, and it means the placeholder and the empty state are always there.
 */
import * as RadixSelect from '@radix-ui/react-select';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUiText } from '@clavian/i18n/react';
import { cx } from '../lib/cx.js';
import { usePortalContainer } from '../theme/theme-provider.js';
import { CheckIcon, ChevronDownIcon } from './icons.js';
import { Field, controlDomProps } from './field.js';
/**
 * While the listbox is open, Radix marks everything outside it `aria-hidden="true"` but leaves it
 * focusable, so Tab can land on a control a screen-reader user cannot see — axe reports it as
 * `aria-hidden-focus`, and it is a real defect, not a false positive. `inert` takes those elements
 * out of the tab order as well.
 *
 * It is applied after the content has mounted (that is when Radix adds its marker) and released
 * synchronously as the listbox starts closing, before Radix puts focus back on the trigger — an
 * effect cleanup would run too late and focus would have nowhere to go.
 */
function useInertBehindListbox(open) {
    const inerted = useRef([]);
    const release = useCallback(() => {
        for (const element of inerted.current)
            element.removeAttribute('inert');
        inerted.current = [];
    }, []);
    useEffect(() => {
        if (!open)
            return undefined;
        const apply = () => {
            for (const element of document.querySelectorAll('[data-aria-hidden="true"]:not([inert])')) {
                element.setAttribute('inert', '');
                inerted.current.push(element);
            }
        };
        // Radix mounts the listbox in a later commit than the one that flips `open`, so the markers
        // may not exist yet when this effect first runs; the observer catches them when they appear.
        apply();
        const observer = new MutationObserver(apply);
        observer.observe(document.body, {
            subtree: true,
            attributes: true,
            attributeFilter: ['data-aria-hidden'],
        });
        return () => {
            observer.disconnect();
            release();
        };
    }, [open, release]);
    return release;
}
export function Select({ options, value, defaultValue, onValueChange, placeholder, disabled, name, id, required, className, ...aria }) {
    const t = useUiText();
    const container = usePortalContainer();
    const [open, setOpen] = useState(false);
    const releaseInert = useInertBehindListbox(open);
    return (_jsxs(RadixSelect.Root, { value: value, defaultValue: defaultValue, onValueChange: onValueChange, disabled: disabled, name: name, required: required, open: open, onOpenChange: (next) => {
            if (!next)
                releaseInert();
            setOpen(next);
        }, children: [_jsxs(RadixSelect.Trigger, { ...aria, id: id, className: cx('clav-select__trigger', className), children: [_jsx(RadixSelect.Value, { placeholder: placeholder ?? t('select.placeholder') }), _jsx(RadixSelect.Icon, { className: "clav-select__chevron", children: _jsx(ChevronDownIcon, {}) })] }), _jsx(RadixSelect.Portal, { container: container, children: _jsx(RadixSelect.Content, { className: "clav-select__content", position: "popper", sideOffset: 4, children: _jsx(RadixSelect.Viewport, { className: "clav-select__viewport", children: options.length === 0 ? (_jsx("div", { className: "clav-select__item", role: "presentation", children: t('select.empty') })) : (options.map((option) => (_jsxs(RadixSelect.Item, { value: option.value, disabled: option.disabled, className: "clav-select__item", children: [_jsx(RadixSelect.ItemText, { children: option.label }), _jsx(RadixSelect.ItemIndicator, { className: "clav-select__indicator", children: _jsx(CheckIcon, {}) })] }, option.value)))) }) }) })] }));
}
/** A labelled select, wired for description and error. */
export function SelectField({ label, description, error, required, showOptional, className, ...select }) {
    return (_jsx(Field, { label: label, description: description, error: error, required: required, showOptional: showOptional, className: className, children: (control) => _jsx(Select, { ...select, ...controlDomProps(control) }) }));
}
//# sourceMappingURL=select.js.map